import React, { useState, useEffect } from "react";
import { Skeleton, Tabs, Empty, Pagination, Tooltip, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getAdminSubscriptionAction } from "@redux/actions/adminActions";
import { manageSubscription } from "@api/subscription/stripe";
import { getInvoices, getPaymentHistory, getPaymentMethods, getSubscriptionHistory, syncBillingData } from "@api/subscription/billing";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  FiCreditCard,
  FiCalendar,
  FiRefreshCw,
  FiClock,
  FiExternalLink,
  FiArrowRight,
  FiCheck,
  FiFileText,
  FiDollarSign,
  FiDownload,
  FiAlertCircle,
  FiChevronRight,
} from "react-icons/fi";

const { TabPane } = Tabs;

const MySubscription = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentSubscription } = useSelector((state) => state.admin) ?? {};
  const [loading, setLoading] = useState(true);
  const [manageLoading, setManageLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Billing data states
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesPagination, setInvoicesPagination] = useState({ page: 1, limit: 5, total: 0 });

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsPagination, setPaymentsPagination] = useState({ page: 1, limit: 5, total: 0 });

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);

  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (user?.role === "admin") {
      fetchAdminSubscription();
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === "invoices" && invoices.length === 0) {
      fetchInvoices();
    } else if (activeTab === "payments" && payments.length === 0) {
      fetchPayments();
    } else if (activeTab === "payment-methods" && paymentMethods.length === 0) {
      fetchPaymentMethods();
    } else if (activeTab === "history" && subscriptionHistory.length === 0) {
      fetchSubscriptionHistory();
    }
  }, [activeTab]);

  // Auto-sync billing data from Stripe when page loads (for paid users)
  const [hasAutoSynced, setHasAutoSynced] = useState(false);

  useEffect(() => {
    const autoSyncBilling = async () => {
      // Only auto-sync for paid users (not Free plan) and only once per page load
      if (currentSubscription?.currentPlan && currentSubscription.currentPlan !== "Free" && !hasAutoSynced) {
        setHasAutoSynced(true);
        try {
          await syncBillingData();

          // Always refresh all data after sync to ensure latest data
          setInvoices([]);
          setPayments([]);
          setPaymentMethods([]);
          setSubscriptionHistory([]);

          // Refetch current tab data
          if (activeTab === "invoices") fetchInvoices();
          else if (activeTab === "payments") fetchPayments();
          else if (activeTab === "payment-methods") fetchPaymentMethods();
          else if (activeTab === "history") fetchSubscriptionHistory();
        } catch (err) {
          // Silent fail - user can manually sync if needed
          console.error("Auto-sync failed", err);
        }
      }
    };

    // Run auto-sync after subscription data is loaded
    if (!loading && currentSubscription) {
      autoSyncBilling();
    }
  }, [loading, currentSubscription?.currentPlan]);

  const fetchAdminSubscription = async () => {
    setLoading(true);
    dispatch(getAdminSubscriptionAction(setLoading));
  };

  const fetchInvoices = async (page = 1) => {
    setInvoicesLoading(true);
    try {
      const res = await getInvoices({ page, limit: invoicesPagination.limit });
      setInvoices(res.data.invoices || []);
      setInvoicesPagination({
        ...invoicesPagination,
        page: res.data.pagination?.page || 1,
        total: res.data.pagination?.total || 0,
      });
    } catch (err) {
      console.error("Failed to fetch invoices", err);
    }
    setInvoicesLoading(false);
  };

  const fetchPayments = async (page = 1) => {
    setPaymentsLoading(true);
    try {
      const res = await getPaymentHistory({ page, limit: paymentsPagination.limit });
      setPayments(res.data.payments || []);
      setPaymentsPagination({
        ...paymentsPagination,
        page: res.data.pagination?.page || 1,
        total: res.data.pagination?.total || 0,
      });
    } catch (err) {
      console.error("Failed to fetch payments", err);
    }
    setPaymentsLoading(false);
  };

  const fetchPaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    try {
      const res = await getPaymentMethods();
      setPaymentMethods(res.data.paymentMethods || []);
    } catch (err) {
      console.error("Failed to fetch payment methods", err);
    }
    setPaymentMethodsLoading(false);
  };

  const fetchSubscriptionHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await getSubscriptionHistory();
      setSubscriptionHistory(res.data.history || []);
    } catch (err) {
      console.error("Failed to fetch subscription history", err);
    }
    setHistoryLoading(false);
  };

  const handleManageSub = (e) => {
    e.preventDefault();
    setManageLoading(true);
    manageSubscription()
      .then((res) => {
        window.location.href = res.data.portalUrl;
      })
      .catch((err) => {
        setManageLoading(false);
        console.log(err);
      });
  };

  const handleSyncBilling = async () => {
    setSyncLoading(true);
    try {
      const res = await syncBillingData();
      const results = res.data.results || {};

      // Build success message showing what was synced
      const parts = [];
      if (results.subscription?.synced) {
        parts.push(`subscription: ${results.subscription.plan}`);
      }
      if (results.invoices?.synced > 0) {
        parts.push(`${results.invoices.synced} invoices`);
      }
      if (results.payments?.synced > 0) {
        parts.push(`${results.payments.synced} payments`);
      }

      message.success(`Synced ${parts.join(", ") || "billing data"}`);

      // Refresh subscription data in Redux store
      fetchAdminSubscription();

      // Refresh billing tab data
      setInvoices([]);
      setPayments([]);
      setPaymentMethods([]);
      setSubscriptionHistory([]);
      // Re-fetch current tab data
      if (activeTab === "invoices") fetchInvoices();
      else if (activeTab === "payments") fetchPayments();
      else if (activeTab === "payment-methods") fetchPaymentMethods();
      else if (activeTab === "history") fetchSubscriptionHistory();
    } catch (err) {
      console.error("Sync failed", err);
      message.error("Failed to sync billing data");
    }
    setSyncLoading(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    // Handle both Unix timestamps and ISO date strings
    if (typeof timestamp === "number") {
      return moment.unix(timestamp).format("MMM DD, YYYY");
    }
    return moment(timestamp).format("MMM DD, YYYY");
  };

  const formatCurrency = (amount, currency = "usd") => {
    if (!amount && amount !== 0) return "-";
    const formatted = (amount / 100).toFixed(2);
    const symbols = { usd: "$", eur: "€", gbp: "£", inr: "₹" };
    return `${symbols[currency] || "$"}${formatted}`;
  };

  const isFreePlan = currentSubscription?.currentPlan === "Free";
  const isLifetime = currentSubscription?.currentPlan === "Lifetime";
  const isActive = currentSubscription?.subscriptionStatus === "active";
  const canManage = !isFreePlan && !isLifetime && currentSubscription?.billingAdmin;

  const planColors = {
    Free: "#64748b",
    Starter: "#3b82f6",
    Pro: "#8b5cf6",
    Business: "#f59e0b",
    Lifetime: "#10b981",
  };

  const currentPlanColor = planColors[currentSubscription?.currentPlan] || "#64748b";

  const getStatusColor = (status) => {
    const colors = {
      paid: { bg: "#f0fdf4", border: "#86efac", text: "#166534" },
      succeeded: { bg: "#f0fdf4", border: "#86efac", text: "#166534" },
      active: { bg: "#f0fdf4", border: "#86efac", text: "#166534" },
      open: { bg: "#fef3c7", border: "#fde68a", text: "#92400e" },
      pending: { bg: "#fef3c7", border: "#fde68a", text: "#92400e" },
      draft: { bg: "#f1f5f9", border: "#cbd5e1", text: "#475569" },
      uncollectible: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b" },
      failed: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b" },
      canceled: { bg: "#f1f5f9", border: "#cbd5e1", text: "#475569" },
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "20px 24px" }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "20px 24px" }}>
      {/* Page Header */}
      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiCreditCard size={20} color="white" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 600, color: "#1e293b" }}>
              {t("profile.subscription.myBillingDetails")}
            </h1>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
              View and manage your subscription billing
            </p>
          </div>
        </div>
        {!isFreePlan && (
          <Tooltip title="Sync billing data from Stripe">
            <button
              onClick={handleSyncBilling}
              disabled={syncLoading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#64748b",
                fontSize: "12px",
                fontWeight: 500,
                cursor: syncLoading ? "not-allowed" : "pointer",
              }}
            >
              <FiRefreshCw size={14} style={{ animation: syncLoading ? "spin 1s linear infinite" : "none" }} />
              {syncLoading ? "Syncing..." : "Sync"}
            </button>
          </Tooltip>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: "16px" }}
        tabBarStyle={{
          background: "#fff",
          padding: "0 16px",
          borderRadius: "12px 12px 0 0",
          border: "1px solid #e2e8f0",
          borderBottom: "none",
          marginBottom: 0,
        }}
      >
        <TabPane
          tab={
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FiCreditCard size={14} /> Overview
            </span>
          }
          key="overview"
        >
          {/* Main Content Card */}
          <div
            style={{
              background: "#fff",
              borderRadius: "0 0 12px 12px",
              border: "1px solid #e2e8f0",
              borderTop: "none",
              overflow: "hidden",
            }}
          >
            {/* Plan Row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: currentPlanColor,
                  }}
                />
                <div>
                  <span style={{ fontSize: "12px", color: "#94a3b8", display: "block" }}>Current Plan</span>
                  <span style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>
                    {currentSubscription?.currentPlan || "Free"}
                  </span>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 10px",
                  borderRadius: "6px",
                  background: isActive ? "#f0fdf4" : "#fef3c7",
                  border: `1px solid ${isActive ? "#86efac" : "#fde68a"}`,
                }}
              >
                <div
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: isActive ? "#10b981" : "#f59e0b",
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: isActive ? "#166534" : "#92400e",
                    textTransform: "capitalize",
                  }}
                >
                  {currentSubscription?.subscriptionStatus || "N/A"}
                </span>
              </div>
            </div>

            {/* Details Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <div
                style={{
                  padding: "14px 20px",
                  borderRight: "1px solid #f1f5f9",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <FiCalendar size={12} color="#94a3b8" />
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Start Date
                  </span>
                </div>
                <span style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b" }}>
                  {isFreePlan
                    ? currentSubscription?.subscription?.startPeriod || "-"
                    : formatDate(currentSubscription?.subscription?.startPeriod)}
                </span>
              </div>

              <div
                style={{
                  padding: "14px 20px",
                  borderRight: "1px solid #f1f5f9",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <FiRefreshCw size={12} color="#94a3b8" />
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Billing Cycle
                  </span>
                </div>
                <span style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b", textTransform: "capitalize" }}>
                  {currentSubscription?.subscription?.billInterval
                    ? currentSubscription?.subscription?.billInterval + "ly"
                    : "-"}
                </span>
              </div>

              <div style={{ padding: "14px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <FiClock size={12} color="#94a3b8" />
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {isFreePlan ? "Next Allocation" : "Next Billing"}
                  </span>
                </div>
                <span style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b" }}>
                  {isFreePlan
                    ? currentSubscription?.subscription?.endPeriod || "-"
                    : formatDate(currentSubscription?.subscription?.endPeriod)}
                </span>
              </div>
            </div>

            {/* Actions Row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 20px",
                background: "#fafbfc",
              }}
            >
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={handleManageSub}
                  disabled={!canManage || manageLoading}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "none",
                    background: canManage ? "#1e293b" : "#e2e8f0",
                    color: canManage ? "#fff" : "#94a3b8",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: canManage ? "pointer" : "not-allowed",
                    transition: "all 0.15s ease",
                  }}
                >
                  {manageLoading ? (
                    <FiRefreshCw size={12} style={{ animation: "spin 1s linear infinite" }} />
                  ) : (
                    <FiCreditCard size={12} />
                  )}
                  {t("profile.subscription.manageSubscription")}
                  {canManage && <FiExternalLink size={10} />}
                </button>

                <Link
                  to="/profile/plans"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    color: "#475569",
                    fontSize: "12px",
                    fontWeight: 500,
                    textDecoration: "none",
                  }}
                >
                  View Plans
                  <FiArrowRight size={10} />
                </Link>
              </div>

              {!currentSubscription?.billingAdmin && !isFreePlan && !isLifetime && (
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>Only billing admin can manage</span>
              )}
            </div>
          </div>

          {/* Upgrade Banner for Free Users */}
          {isFreePlan && (
            <div
              style={{
                marginTop: "16px",
                padding: "14px 18px",
                background: "#fff",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiArrowRight size={14} color="#fff" />
                </div>
                <div>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", display: "block" }}>
                    Upgrade your plan
                  </span>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>Get more features and higher limits</span>
                </div>
              </div>
              <Link
                to="/profile/plans"
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Upgrade
              </Link>
            </div>
          )}

          {/* Lifetime Badge */}
          {isLifetime && (
            <div
              style={{
                marginTop: "16px",
                padding: "14px 18px",
                background: "#f0fdf4",
                borderRadius: "10px",
                border: "1px solid #86efac",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <FiCheck size={18} color="#10b981" />
              <span style={{ fontSize: "13px", color: "#166534" }}>
                <strong>Lifetime access</strong> - Enjoy all features forever with no recurring charges
              </span>
            </div>
          )}
        </TabPane>

        {/* Invoices Tab */}
        <TabPane
          tab={
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FiFileText size={14} /> Invoices
            </span>
          }
          key="invoices"
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "0 0 12px 12px",
              border: "1px solid #e2e8f0",
              borderTop: "none",
              overflow: "hidden",
            }}
          >
            {invoicesLoading ? (
              <div style={{ padding: "24px" }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </div>
            ) : invoices.length === 0 ? (
              <div style={{ padding: "40px 24px" }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: "#94a3b8" }}>
                      {isFreePlan ? "Upgrade to a paid plan to see invoices" : "No invoices found"}
                    </span>
                  }
                />
              </div>
            ) : (
              <>
                {/* Invoice List */}
                {invoices.map((invoice, index) => {
                  const statusColors = getStatusColor(invoice.status);
                  return (
                    <div
                      key={invoice.id || index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "14px 20px",
                        borderBottom: index < invoices.length - 1 ? "1px solid #f1f5f9" : "none",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#fafbfc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            background: "#f1f5f9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FiFileText size={16} color="#64748b" />
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>
                              {invoice.number || `Invoice ${invoice.id?.slice(-8)}`}
                            </span>
                            <span
                              style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                background: statusColors.bg,
                                border: `1px solid ${statusColors.border}`,
                                color: statusColors.text,
                                textTransform: "capitalize",
                              }}
                            >
                              {invoice.status}
                            </span>
                          </div>
                          <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                            {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                          {formatCurrency(invoice.amountPaid || invoice.amountDue, invoice.currency)}
                        </span>

                        {invoice.pdfUrl && (
                          <Tooltip title="Download PDF">
                            <a
                              href={invoice.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "#f1f5f9",
                                color: "#64748b",
                                transition: "all 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#1e293b";
                                e.currentTarget.style.color = "#fff";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f1f5f9";
                                e.currentTarget.style.color = "#64748b";
                              }}
                            >
                              <FiDownload size={14} />
                            </a>
                          </Tooltip>
                        )}

                        {invoice.hostedUrl && (
                          <Tooltip title="View Invoice">
                            <a
                              href={invoice.hostedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "#f1f5f9",
                                color: "#64748b",
                                transition: "all 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#1e293b";
                                e.currentTarget.style.color = "#fff";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f1f5f9";
                                e.currentTarget.style.color = "#64748b";
                              }}
                            >
                              <FiExternalLink size={14} />
                            </a>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {invoicesPagination.total > invoicesPagination.limit && (
                  <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9", textAlign: "right" }}>
                    <Pagination
                      size="small"
                      current={invoicesPagination.page}
                      total={invoicesPagination.total}
                      pageSize={invoicesPagination.limit}
                      onChange={(page) => fetchInvoices(page)}
                      showSizeChanger={false}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </TabPane>

        {/* Payments Tab */}
        <TabPane
          tab={
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FiDollarSign size={14} /> Payments
            </span>
          }
          key="payments"
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "0 0 12px 12px",
              border: "1px solid #e2e8f0",
              borderTop: "none",
              overflow: "hidden",
            }}
          >
            {paymentsLoading ? (
              <div style={{ padding: "24px" }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </div>
            ) : payments.length === 0 ? (
              <div style={{ padding: "40px 24px" }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: "#94a3b8" }}>
                      {isFreePlan ? "Upgrade to a paid plan to see payments" : "No payments found"}
                    </span>
                  }
                />
              </div>
            ) : (
              <>
                {/* Payment List */}
                {payments.map((payment, index) => {
                  const statusColors = getStatusColor(payment.status);
                  return (
                    <div
                      key={payment.id || index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "14px 20px",
                        borderBottom: index < payments.length - 1 ? "1px solid #f1f5f9" : "none",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#fafbfc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            background: statusColors.bg,
                            border: `1px solid ${statusColors.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {payment.status === "succeeded" ? (
                            <FiCheck size={16} color={statusColors.text} />
                          ) : payment.status === "failed" ? (
                            <FiAlertCircle size={16} color={statusColors.text} />
                          ) : (
                            <FiDollarSign size={16} color={statusColors.text} />
                          )}
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>
                              Payment {payment.status === "succeeded" ? "Successful" : payment.status}
                            </span>
                            {payment.paymentMethod?.brand && (
                              <span style={{ fontSize: "11px", color: "#94a3b8", textTransform: "capitalize" }}>
                                **** {payment.paymentMethod.last4}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: "12px", color: "#94a3b8" }}>{formatDate(payment.createdAt)}</span>
                          {payment.failureMessage && (
                            <span style={{ fontSize: "11px", color: "#ef4444", display: "block" }}>
                              {payment.failureMessage}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                          {formatCurrency(payment.amount, payment.currency)}
                        </span>

                        {payment.receiptUrl && (
                          <Tooltip title="View Receipt">
                            <a
                              href={payment.receiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "#f1f5f9",
                                color: "#64748b",
                                transition: "all 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#1e293b";
                                e.currentTarget.style.color = "#fff";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f1f5f9";
                                e.currentTarget.style.color = "#64748b";
                              }}
                            >
                              <FiExternalLink size={14} />
                            </a>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {paymentsPagination.total > paymentsPagination.limit && (
                  <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9", textAlign: "right" }}>
                    <Pagination
                      size="small"
                      current={paymentsPagination.page}
                      total={paymentsPagination.total}
                      pageSize={paymentsPagination.limit}
                      onChange={(page) => fetchPayments(page)}
                      showSizeChanger={false}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </TabPane>

        {/* Payment Methods Tab */}
        <TabPane
          tab={
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FiCreditCard size={14} /> Payment Methods
            </span>
          }
          key="payment-methods"
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "0 0 12px 12px",
              border: "1px solid #e2e8f0",
              borderTop: "none",
              overflow: "hidden",
            }}
          >
            {paymentMethodsLoading ? (
              <div style={{ padding: "24px" }}>
                <Skeleton active paragraph={{ rows: 2 }} />
              </div>
            ) : paymentMethods.length === 0 ? (
              <div style={{ padding: "40px 24px" }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<span style={{ color: "#94a3b8" }}>No payment methods saved</span>}
                />
                {canManage && (
                  <div style={{ textAlign: "center", marginTop: "16px" }}>
                    <button
                      onClick={handleManageSub}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        border: "none",
                        background: "#1e293b",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      <FiCreditCard size={12} />
                      Add Payment Method
                      <FiExternalLink size={10} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Payment Methods List */}
                {paymentMethods.map((method, index) => (
                  <div
                    key={method.id || index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "14px 20px",
                      borderBottom: index < paymentMethods.length - 1 ? "1px solid #f1f5f9" : "none",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div
                        style={{
                          width: "44px",
                          height: "30px",
                          borderRadius: "4px",
                          background: "#f1f5f9",
                          border: "1px solid #e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#64748b",
                          textTransform: "uppercase",
                        }}
                      >
                        {method.brand || method.type || "Card"}
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>
                            **** **** **** {method.last4}
                          </span>
                          {method.isDefault && (
                            <span
                              style={{
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                background: "#f0fdf4",
                                border: "1px solid #86efac",
                                color: "#166534",
                              }}
                            >
                              Default
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                          Expires {method.expMonth?.toString().padStart(2, "0")}/{method.expYear?.toString().slice(-2)}
                        </span>
                      </div>
                    </div>

                    {canManage && (
                      <button
                        onClick={handleManageSub}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "6px 10px",
                          borderRadius: "4px",
                          border: "1px solid #e2e8f0",
                          background: "#fff",
                          color: "#64748b",
                          fontSize: "11px",
                          cursor: "pointer",
                        }}
                      >
                        Manage
                        <FiChevronRight size={10} />
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </TabPane>

        {/* Subscription History Tab */}
        <TabPane
          tab={
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FiClock size={14} /> History
            </span>
          }
          key="history"
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "0 0 12px 12px",
              border: "1px solid #e2e8f0",
              borderTop: "none",
              overflow: "hidden",
            }}
          >
            {historyLoading ? (
              <div style={{ padding: "24px" }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </div>
            ) : subscriptionHistory.length === 0 ? (
              <div style={{ padding: "40px 24px" }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={<span style={{ color: "#94a3b8" }}>No subscription changes yet</span>}
                />
              </div>
            ) : (
              <>
                {/* History Timeline */}
                {subscriptionHistory.map((entry, index) => {
                  const statusColors = getStatusColor(entry.status);
                  const planColor = planColors[entry.plan] || "#64748b";

                  return (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        padding: "14px 20px",
                        borderBottom: index < subscriptionHistory.length - 1 ? "1px solid #f1f5f9" : "none",
                      }}
                    >
                      {/* Timeline indicator */}
                      <div style={{ position: "relative", marginRight: "16px" }}>
                        <div
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            background: planColor,
                            marginTop: "4px",
                          }}
                        />
                        {index < subscriptionHistory.length - 1 && (
                          <div
                            style={{
                              position: "absolute",
                              top: "18px",
                              left: "4px",
                              width: "2px",
                              height: "calc(100% + 14px)",
                              background: "#e2e8f0",
                            }}
                          />
                        )}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                          <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>
                            {entry.plan || "Unknown Plan"}
                          </span>
                          <span
                            style={{
                              fontSize: "10px",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              background: statusColors.bg,
                              border: `1px solid ${statusColors.border}`,
                              color: statusColors.text,
                              textTransform: "capitalize",
                            }}
                          >
                            {entry.action || entry.status}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <span style={{ fontSize: "12px", color: "#94a3b8" }}>{formatDate(entry.startDate)}</span>
                          {entry.endDate && (
                            <>
                              <span style={{ color: "#cbd5e1" }}>→</span>
                              <span style={{ fontSize: "12px", color: "#94a3b8" }}>{formatDate(entry.endDate)}</span>
                            </>
                          )}
                          {entry.interval && (
                            <span
                              style={{ fontSize: "11px", color: "#64748b", textTransform: "capitalize" }}
                            >
                              {entry.interval}ly billing
                            </span>
                          )}
                        </div>
                      </div>

                      {entry.amount > 0 && (
                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#64748b" }}>
                          {formatCurrency(entry.amount, entry.currency)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </TabPane>
      </Tabs>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .ant-tabs-tab {
          padding: 12px 0 !important;
        }
        .ant-tabs-tab-btn {
          color: #64748b !important;
          font-size: 13px !important;
        }
        .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #1e293b !important;
          font-weight: 500 !important;
        }
        .ant-tabs-ink-bar {
          background: #8b5cf6 !important;
        }
      `}</style>
    </div>
  );
};

export default MySubscription;
